import { limitPerPage } from '~/helpers/constants';
import {
  convertKeyValueToFormData,
  convertKeyValueToFormDataV2
} from '~/helpers/helpers';
import { emperor } from '~/services';
import {
  Building,
  BuildingDashboardConfigResponse,
  IAttributedBuildingKeyValue,
  IBuildingAddress,
  IBuildingEmailDetails,
  IBuildingEmailGroup,
  IBuildingEmailRole,
  IBuildingKey,
  IBuildingKeyAttribute,
  IHelloData,
  UpdateBuildingDashboardConfig
} from '~/types/building';
import { GatheringFormType } from '~/types/gathering';
import { Comment } from '~/types/comment';
import { AxiosResponse, CancelToken } from 'axios';
import { TenantInfo } from '~/containers/RentRoll/TenantRentRollTable';
import { IBuildingHelloData } from '~/types/helloData';
import {
  IBuildingTile,
  IBuildingTileForm
} from '~/containers/MyBuildingTiles/interfaces';
import { IAlnData } from '~/containers/CreateBuilding/interfaces';

export type BuildingWithUserMissingTenantKey = {
  uuid: string;
  name: string;
  totalUsersMissingTenantKey: number;
};

export interface IBuildingOnBoarding {
  buildingUuid: string; // The uuid for the building
  onboardingSkippable?: boolean; // on boarding config
  buildingGoLive?: string; // on boarding config
  raffleDescription: string; // on boarding config
  raffleEndDate: string; // on boarding config
  raffleAmount: string; // on boarding config
}

export interface IRaffle {
  uuid: string;
  description: string;
  building: string;
  endDate: string;
  amount: string;
  active: boolean;
}

export const fetchBuildingsRequest = async (cancelToken?: CancelToken) => {
  return await emperor.get('/v2/user/buildings', { cancelToken });
};

export const fetchBuildingsPageRequest = async (params: {
  page: number;
  pageSize: number;
  q?: string;
}) => {
  return await emperor.get<{ buildings: Building[]; total: number }>(
    '/v2/user/buildings',
    { params }
  );
};

export const lookupBuildings = async (
  filter: { ids: string[] } | { names: string[] }
) => {
  const res = await emperor.post<Building[]>('/v2/user/buildings', filter);
  return res.data;
};

export const toBuildingOption = (b: Building) => ({
  value: b.uuid,
  label: b.name
});

export const searchBuildingOptions = async (q: string) => {
  const res = await fetchBuildingsPageRequest({ page: 1, pageSize: 20, q });
  return res.data.buildings.map(toBuildingOption);
};

export const getBuildingNames = async () => {
  return await emperor.get('/building/get-names');
};

export const fetchGatheringsRequest = async (
  buildingId: string,
  view?: string,
  sort?: string
) => {
  return await emperor.get(`v2/building/${buildingId}/gatherings`, {
    params: {
      sort: sort ?? 'startTime ASC',
      cancelled: false,
      view
    }
  });
};

export const createGatheringRequest = async (gathering: GatheringFormType) => {
  if (gathering.commitType != '45890325-4e1d-4364-8aea-a13b2cf4c02c') {
    gathering.isRSVP = true;
  } else {
    gathering.isRSVP = false;
  }

  return await emperor.post(
    '/groupevent',
    convertKeyValueToFormDataV2(gathering)
  );
};

export const fetchBuildingGroups = async (buildingId: string) => {
  return await emperor.get('/group', {
    params: { building: [buildingId], active: true }
  });
};

export const fetchBuildingEventTypes = async (buildingId: string) => {
  return await emperor.get('/event-types', {
    params: { building: buildingId }
  });
};

export const deleteGatheringRequest = async (gatheringId: string) => {
  return await emperor.delete(`/groupevent/${gatheringId}`);
};

export const cancelRecurringGatheringRequest = async (gatheringId: string) => {
  return await emperor.patch(
    `/groupevent/${gatheringId}/cancel-recurring-event`
  );
};

export const updateGathering = async (gathering: GatheringFormType) => {
  if (gathering.commitType != '45890325-4e1d-4364-8aea-a13b2cf4c02c') {
    gathering.isRSVP = true;
  } else {
    gathering.isRSVP = false;
  }

  return await emperor.patch(
    `/groupevent/${gathering.uuid}`,
    convertKeyValueToFormDataV2(gathering)
  );
};

export const fetchBuildingStats = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/stats`);
};

export const fetchBuildingTier = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/tier`);
};

export const fetchBuildingDetails = async (
  buildingId: string,
  min?: boolean
) => {
  const params = min && { returnData: 'min' };
  return await emperor.get(`v2/user/building/${buildingId}`, {
    params
  });
};

export const getBuildingsWithTotalUsersMissingTenantKey = async () => {
  return await emperor.get<{ buildings: BuildingWithUserMissingTenantKey[] }>(
    '/resident-management/buildings/total-users-missing-tenantkey'
  );
};

export const fetchSentimentComments = async (
  sentiment: string,
  buildingId: string,
  page: number
): Promise<Comment[]> => {
  const params = {
    page,
    sentiment
  };
  const res = await emperor.get(
    `/building/${buildingId}/comments-by-sentiment`,
    {
      params
    }
  );

  return res.data;
};

export const updateBuilding = async (
  buildingPatch: Object,
  buildingId: string
) => {
  const data = convertKeyValueToFormData(buildingPatch);
  return emperor.patch(`/building/${buildingId}`, data);
};

export const updateBuildingImage = async (
  buildingPatch: Object,
  buildingId: string
) => {
  const data = convertKeyValueToFormDataV2(buildingPatch);
  return emperor.patch(`/building/${buildingId}`, data);
};

export const updateBuildingUnits = async (
  buildingPatch: Object,
  buildingId: string
) => {
  return emperor.patch(`/building/${buildingId}/unit`, buildingPatch);
};

export const setCommenterRequest = async (
  buildingId: string,
  userEmail: string,
  type: string
) => {
  return emperor.post(`/building/${buildingId}/assign-commenter`, {
    buildingId,
    userEmail,
    type
  });
};

export const fetchBuildingDashboardConfig = async (
  buildingId: string
): Promise<AxiosResponse<BuildingDashboardConfigResponse>> => {
  const res = await emperor.get(`/buildingdashboardconfig/${buildingId}`);
  return res;
};

export const fetchInactiveResidentCount = async (
  buildingId: string
): Promise<AxiosResponse<{ count: number }>> => {
  return await emperor.get(`/building/${buildingId}/inactive-resident-count`);
};

export const fetchBuildingMerchants = async (buildingId: string) => {
  return await emperor.get('/merchant', {
    params: { building: buildingId, returnData: 'min' }
  });
};

export const fetchGatheringFeedback = async (gatheringId: string) => {
  return await emperor.get('/v2/gatheringFeedback', {
    params: {
      gathering: gatheringId
    }
  });
};

export const fetchCommentsRequest = async (
  building: string,
  page: number,
  otherParams = {}
) => {
  return await emperor.get(`/globalcomment/dash`, {
    params: { page, limit: limitPerPage, building, ...otherParams }
  });
};

export const fetchCommentThreadRequest = async (
  id: string
): Promise<{ status: number; data: Comment[] }> => {
  return await emperor.get(`/groupcomment/${id}/thread`);
};

export const fetchGroupEventCommentThreadRequest = async (
  id: string
): Promise<{ status: number; data: Comment[] }> => {
  return await emperor.get(`/groupeventcomment/${id}/thread`);
};

export const fetchCommentsWithTimeFilterRequest = async (
  building: string,
  timeFilter: number,
  otherParams = {}
) => {
  return await emperor.get(`/globalcomment/dash`, {
    params: { timeFilter, building, ...otherParams }
  });
};

export const fetchUserCountByBuilding = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/resident-count`);
};

export const fetchPositiveResidentsRequest = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/review-eligible-residents`);
};

export const removeResidentFormResidentsTabRequest = async (data: {
  user: string;
  removalReason: string;
  removalTable: string;
  bypassCheck?: boolean;
}) => {
  return await emperor.post(`resident-removed`, data);
};

export const pauseResidentOutreachRequest = async (data: {
  user: string;
  pausedUntil: string;
}) => {
  return await emperor.post(`resident-removed/pause`, data);
};

export const resumeResidentOutreachRequest = async (userId: string) => {
  return await emperor.delete(`resident-removed/pause/${userId}`);
};

export const fetchPausedResidentOutreachRequest = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/paused-residents`);
};

export const deactivateResidentByBuilding = async (data: {
  building: string;
}) => {
  return await emperor.post(`building/deactivate-residents`, data);
};

export const deactivateBuilding = async (data: { building: string }) => {
  return await emperor.patch(`building/${data.building}/deactivate`, data);
};

export const updateDashboardConfig = async (
  buildingId: string,
  config: UpdateBuildingDashboardConfig
) => {
  return await emperor.patch(`buildingdashboardconfig/${buildingId}`, {
    config
  });
};

// Deprecated: replaced by the global master template flow.
//
// export const addGatheringTemplates = async (buildingId: string) => {
//   return await emperor.post(`building/clone-gathering-templates`, {
//     id: buildingId
//   });
// };
//
// export const removeGatheringTemplates = async (buildingId: string) => {
//   return await emperor.delete(
//     `building/clone-gathering-templates/${buildingId}`
//   );
// };

export const sendInviteRequest = async (user: string) => {
  return await emperor.post('invitation', { user });
};

export const fetchResidentsWithReviewInvitationRequest = async (
  buildingId: string,
  page: number
) => {
  return await emperor.get(`/building/${buildingId}/invitation`, {
    params: { limit: limitPerPage, page }
  });
};

export const fetchGatheringComments = async (
  gatheringId: string,
  gatheringDate?: string,
  otherParams = {}
) => {
  return await emperor.get(`/groupeventcomment`, {
    params: {
      groupEvent: gatheringId,
      ...(gatheringDate && {
        interestedDate: gatheringDate
      }),
      ...otherParams
    }
  });
};

export const fetchCommentSentimentStats = async (building: string) => {
  return await emperor.get(`/building/${building}/comment-sentiment`, {
    params: { timeFilter: 30 }
  });
};

export const postCreateBuilding = async (building: FormData) => {
  return await emperor.post(`/building`, building);
};

export const patchEditBuilding = async (
  buildingId: string,
  building: FormData
) => {
  return await emperor.patch(`/building/${buildingId}`, building);
};

export const fetchUsersNotLinkedWithTenant = async (buildingId: string) => {
  return await emperor.get(
    `/v2/building/${buildingId}/users/missing-tenant-info`
  );
};

export const searchTenant = async (buildingId: string, searchText: string) => {
  return emperor.get(
    `/buildings/${buildingId}/tenant-search?search=${searchText}`
  );
};

export const bindUserWithTenant = async (
  buildingId: string,
  matches: { userId: string; tenant: TenantInfo }[]
) => {
  return emperor.patch(
    `/buildings/${buildingId}/manually-bind-tenant-with-user`,
    {
      matches
    }
  );
};

export const fetchBuildingDetailsById = async (
  buildingId: string
): Promise<AxiosResponse<Building>> => {
  return await emperor.get('/building/find-one-building', {
    params: { buildingId }
  });
};

export const getBuildingEmailRoles = async (): Promise<
  AxiosResponse<IBuildingEmailRole[]>
> => {
  return await emperor.get('/building/get-building-email-roles');
};

export const getBuildingEmailGroups = async (): Promise<
  AxiosResponse<IBuildingEmailGroup[]>
> => {
  return await emperor.get('/building/get-building-email-groups');
};

export const getBuildingEmailDetails = async (
  buildingUuid: string
): Promise<AxiosResponse<IBuildingEmailDetails>> => {
  return await emperor.get('/building/get-email-details', {
    params: { buildingUuid }
  });
};

export const postBuildingEmailDetails = async (
  buildingUuid: string,
  buildingDetails: Partial<IBuildingEmailDetails>
): Promise<AxiosResponse<IBuildingEmailDetails>> => {
  return await emperor.post('/building/post-email-details', {
    buildingUuid,
    ...buildingDetails
  });
};

export interface BuildingEmailCsvPreview {
  missingColumns: string[];
  stats: { total: number; toAdd: number; toUpdate: number; invalid: number };
  rows: {
    rowNumber: number;
    buildingName: string;
    emailType: string;
    email: string;
    role: string;
    status: 'add' | 'update' | 'invalid';
    errors: string[];
  }[];
  canUpload: boolean;
}

// dryRun goes in the query: multipart text fields sent after the file are dropped server-side.
export const postBuildingEmailsCsv = async (
  file: File,
  dryRun: boolean
): Promise<AxiosResponse<BuildingEmailCsvPreview>> => {
  const formData = new FormData();
  formData.append('csvFile', file);
  return emperor.post('building/bulk-upload-emails', formData, {
    params: { dryRun }
  });
};

export const putBuildingAddress = async (
  buildingId: string,
  address: IBuildingAddress
): Promise<AxiosResponse<IBuildingAddress>> => {
  return await emperor.put('/building/address', { ...address, buildingId });
};

export const getBuildingAddress = async (
  buildingId: string
): Promise<AxiosResponse<IBuildingAddress>> => {
  return await emperor.get('/building/address', { params: { buildingId } });
};

export const patchBuildingEmailGroups = async (
  buildingUuid: string,
  buildingDetails: Partial<IBuildingEmailDetails>
): Promise<AxiosResponse<IBuildingEmailDetails>> => {
  return await emperor.patch('/building/patch-email-groups', {
    buildingUuid,
    ...buildingDetails
  });
};

export const postEnableHelloData = async (
  buildingId: string,
  enableHelloData: boolean
): Promise<AxiosResponse<void>> => {
  return await emperor.post('/building/enable-hello-data', {
    enableHelloData,
    buildingId
  });
};

export const getBuildingHelloData = async (
  buildingId: string
): Promise<AxiosResponse<IBuildingHelloData>> => {
  return await emperor.get('/building/get-hello-data', {
    params: { buildingId }
  });
};

export const getBuildingTiles = async (
  buildingId: string
): Promise<AxiosResponse<IBuildingTile[]>> => {
  return await emperor.get('/building/building-tiles', {
    params: { buildingId }
  });
};

export const putBuildingTile = async (
  formData: FormData
): Promise<AxiosResponse<IBuildingTile>> => {
  return await emperor.put('/building/building-tile', formData);
};

export const deleteBuildingTile = async (
  tileUuid: string
): Promise<AxiosResponse<IBuildingTile>> => {
  return await emperor.delete('/building/building-tile', {
    params: { tileUuid: tileUuid }
  });
};

export const sortBuildingTile = async (
  byTileIdSortOrder: Record<string, number>
): Promise<AxiosResponse<void>> => {
  return await emperor.patch('/building/sort-building-tiles', {
    byTileidSortOrder: JSON.stringify(byTileIdSortOrder)
  });
};

export const getBuildingKeys = async (): Promise<
  AxiosResponse<IBuildingKey[]>
> => {
  return await emperor.get('/building/keys');
};

export const postBuildingKeys = async (
  buildingKeys: IBuildingKey[]
): Promise<AxiosResponse<void>> => {
  return await emperor.post('/building/keys', { buildingKeys });
};

export const getBuildingKeyAttributes = async (
  buildingUuid: string
): Promise<AxiosResponse<IBuildingKeyAttribute>> => {
  return await emperor.get('/building/key-attributes', {
    params: { buildingId: buildingUuid }
  });
};

export const getGenericBuildingKeyAttributes = async (): Promise<
  AxiosResponse<Record<string, string[]>>
> => {
  return await emperor.get('/building/generic-key-attributes');
};

export const postBuildingKeyAttributes = async (
  buildingUuid: string,
  keyAttributes: Partial<IBuildingKeyAttribute>
): Promise<AxiosResponse<IBuildingKeyAttribute>> => {
  return await emperor.post('/building/key-attributes', {
    ...keyAttributes,
    buildingUuid
  });
};

export const getBuildingConfig = async (buildingId: string) => {
  return await emperor.get(`/buildingconfig/${buildingId}`);
};

// Update Building Config
export const updateBuildingConfig = async (
  buildingConfigId: string,
  data: any
) => {
  return await emperor.patch(`/buildingconfig/${buildingConfigId}`, data);
};

export const fetchBuildingEmojis = async (buildingUuid: string) => {
  return await emperor.get('/building/emojis', { params: { buildingUuid } });
};

export const getAlnProperties = async (
  searchQuery: string
): Promise<AxiosResponse<IAlnData[]>> => {
  return await emperor.get(`/building/aln-properties`, {
    params: { searchQuery }
  });
};

export const getPropertyHelloData = async (
  name: string,
  postalcode: string,
  state: string
): Promise<AxiosResponse<IHelloData>> => {
  return await emperor.get(`/building/property-hello-data`, {
    params: { name, postalcode, state }
  });
};

export const postPropertyOnBoarding = async (
  onBoardingData: FormData
): Promise<AxiosResponse<Building>> => {
  return await emperor.post(`/building/onboarding`, onBoardingData);
};

export const getBuildingRaffle = async (
  building: string
): Promise<AxiosResponse<IRaffle[]>> => {
  return await emperor.get(`/v2/raffle`, {
    params: { building, active: true }
  });
};

export const updateHowToGuideConfig = async (
  buildingId: string,
  link: string | null
): Promise<AxiosResponse> => {
  return await emperor.patch(`/how-to-guide-config`, { buildingId, link });
};

export const triggerPartnerSync = async (
  buildingId: string
): Promise<AxiosResponse> => {
  return await emperor.post(`/external-partner/building/${buildingId}/sync`);
};

export const triggerPartnerFeedback = async (
  buildingId: string
): Promise<AxiosResponse> => {
  return await emperor.post(`/external-partner/building/${buildingId}/trigger-feedback`);
};

interface BulkImportPreviewRow {
  rowNumber: number;
  name: string | null;
  status: 'create' | 'finish' | 'skip' | 'invalid';
  errors: string[];
}

export interface BulkImportPreview {
  missingColumns: string[];
  stats: {
    total: number;
    toCreate: number;
    toFinish: number;
    toSkip: number;
    invalid: number;
  };
  rows: BulkImportPreviewRow[];
  canImport: boolean;
}

export interface BulkImportStatusRow {
  rowNumber: number;
  buildingName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'stuck';
  result: {
    outcome: 'created' | 'skipped' | 'partial';
    buildingUuid: string;
    flyerLogo?: 'used' | 'skipped';
    joinLink?: 'created' | 'failed';
    reviewStatus?: 'matched' | 'needsReview' | 'notFound' | 'skipped' | 'error';
    reviewUrl?: string | null;
  } | null;
  error: string | null;
}

export interface BulkImportStatus {
  queueUuid: string | null;
  active: boolean;
  abandoned: boolean;
  total: number;
  completed: number;
  failed: number;
  rows: BulkImportStatusRow[];
}

// dryRun goes in the query: multipart text fields sent after the file are dropped server-side.
const postBuildingBulkImport = (file: File, dryRun: boolean) => {
  const formData = new FormData();
  formData.append('csvFile', file);
  return emperor.post('building/bulk-import', formData, { params: { dryRun } });
};

export const validateBuildingBulkImport = async (
  file: File
): Promise<{ data: BulkImportPreview }> => postBuildingBulkImport(file, true);

export const startBuildingBulkImport = async (
  file: File
): Promise<{ data: { queueUuid: string; jobCount: number } }> =>
  postBuildingBulkImport(file, false);

export const getBuildingBulkImportStatus = async (): Promise<{
  data: BulkImportStatus;
}> => emperor.get('building/bulk-import/status');
